import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import {useMemo} from 'react';
import toast from 'react-hot-toast';

import {RqGetAllProducts} from '../../../../../reactQuery/apis';
import {RqCreateBatch} from '../../../../../reactQuery/apis/rqProductionAPI';

const {Text} = Typography;

const PRIMARY = '#E83F25';
const PRIMARY_LIGHT = '#fef2f2';

const SectionTitle = ({children}) => (
  <div className={'flex items-center gap-2 mb-3'}>
    <div className={'w-1 h-4 rounded-full'} style={{background: PRIMARY}} />
    <Text className={'font-bold text-gray-700 uppercase tracking-widest text-xs'}>
      {children}
    </Text>
  </div>
);

SectionTitle.propTypes = {
  children: PropTypes.node,
};

const CreateBatchModal = ({
  isModalVisible,
  handleCancel,
  form,
  token,
  onBatchCreated,
}) => {
  const createBatchMutation = RqCreateBatch();

  /* Fetch raw material products */
  const {data: allProducts = [], isLoading: productsLoading} = RqGetAllProducts();

  const rawProductOptions = useMemo(
    () =>
      allProducts.map(p => ({
        label: `${p.productName} (Stock: ${p.quantity ?? 0} KG)`,
        value: p.id,
        productName: p.productName,
        stock: p.quantity ?? 0,
      })),
    [allProducts],
  );

  const selectedIngredients = Form.useWatch('ingredientProductIds', form) || [];

  const onFinish = async values => {
    try {
      const ingredients = (values.ingredientProductIds || []).map(pid => {
        const rowValues = values.ingredientDetails?.[pid] || {};
        return {
          productId: pid,
          quantity: rowValues.quantity ?? 0,
        };
      });

      const payload = {
        batchNumber: values.batchNumber,
        batchName: values.batchName,
        batchDate: values.batchDate ? values.batchDate.valueOf() : Date.now(),
        producedQuantity: values.producedQuantity ?? 0,
        ingredients,
      };

      await createBatchMutation.mutateAsync({payload, token});
      toast.success('Stage 1 Bulk Batch created successfully!');

      if (onBatchCreated) {onBatchCreated(payload);}

      form.resetFields();
      handleCancel();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || 'Failed to create bulk batch',
      );
    }
  };

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-3 py-1'}>
          <div
            className={'w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}
          >
            🧪
          </div>
          <div>
            <div className={'font-bold text-gray-800 text-base'}>Stage 1: Bulk Batch Creation</div>
            <div className={'text-gray-400 font-normal text-xs'}>
              Manufacture bulk pigment powder/liquid yield from raw materials
            </div>
          </div>
        </div>
      )}
      open={isModalVisible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={'Create Bulk Batch'}
      okButtonProps={{
        className: 'bg-red-600 hover:!bg-red-700 text-white px-6',
        loading: createBatchMutation.isPending || createBatchMutation.isLoading,
      }}
      cancelButtonProps={{className: 'rounded-md'}}
      width={800}
      destroyOnClose
      styles={{
        header: {borderBottom: `3px solid ${PRIMARY_LIGHT}`, paddingBottom: 12},
        body: {padding: '16px 20px', maxHeight: '78vh', overflowY: 'auto'},
      }}
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={onFinish}
        initialValues={{
          batchNumber: `BATCH-${Date.now().toString().slice(-5)}`,
        }}
      >
        <div className={'rounded-md p-4 mb-4 bg-red-50/50 border border-red-100'}>
          <SectionTitle>Batch Information</SectionTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={'batchNumber'}
                label={<span className={'font-semibold text-slate-600'}>Batch Number</span>}
                rules={[{required: true, message: 'Batch number is required'}]}
              >
                <Input placeholder={'e.g. BATCH-Y101'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={'batchDate'}
                label={<span className={'font-semibold text-slate-600'}>Batch Date & Time</span>}
                rules={[{required: true, message: 'Batch date is required'}]}
              >
                <DatePicker
                  className={'w-full'}
                  showTime
                  format={'YYYY-MM-DD HH:mm'}
                  placeholder={'Select batch date'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={14}>
              <Form.Item
                name={'batchName'}
                label={<span className={'font-semibold text-slate-600'}>Batch Description / Name</span>}
                rules={[{required: true, message: 'Batch name is required'}]}
              >
                <Input placeholder={'e.g. Bulk Natural Yellow Curcumin Powder Batch'} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name={'producedQuantity'}
                label={<span className={'font-semibold text-slate-600'}>Bulk Yield Produced (KG)</span>}
                rules={[
                  {required: true, message: 'Produced yield quantity is required'},
                  {type: 'number', min: 0.01, message: 'Must be greater than 0'},
                ]}
              >
                <InputNumber min={0.01} step={0.01} className={'w-full'} placeholder={'e.g. 1000'} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className={'rounded-md p-4 border border-gray-200 bg-white'}>
          <SectionTitle>Raw Material Ingredients Consumed</SectionTitle>

          <Form.Item
            name={'ingredientProductIds'}
            label={<span className={'font-semibold text-slate-600'}>Select Raw Materials</span>}
            rules={[{required: true, message: 'Select at least one raw material ingredient'}]}
          >
            <Select
              mode={'multiple'}
              showSearch
              loading={productsLoading}
              options={rawProductOptions}
              placeholder={'Search and select raw material ingredients…'}
              optionFilterProp={'label'}
              allowClear
            />
          </Form.Item>

          {selectedIngredients.length > 0 && (
            <div className={'mt-3 space-y-3'}>
              <Text className={'text-xs text-slate-500 font-semibold tracking-wide'}>
                Specify Consumed Quantity (KG) for Each Raw Material
              </Text>
              {selectedIngredients.map(pid => {
                const opt = rawProductOptions.find(o => o.value === pid);
                return (
                  <div
                    key={pid}
                    className={'flex items-center justify-between p-3 rounded-md border border-gray-100 bg-gray-50'}
                  >
                    <div>
                      <Text className={'font-medium text-gray-800 text-sm block'}>
                        {opt?.productName ?? pid}
                      </Text>
                      <Text className={'text-xs text-gray-500'}>
                        Available Stock: <span className={'font-semibold text-gray-700'}>{opt?.stock} KG</span>
                      </Text>
                    </div>
                    <Space direction={'horizontal'} align={'center'}>
                      <Form.Item
                        name={['ingredientDetails', pid, 'quantity']}
                        noStyle
                        rules={[{required: true, message: 'Required'}]}
                      >
                        <InputNumber
                          min={0.01}
                          step={0.01}
                          placeholder={'Qty (KG)'}
                          className={'w-36'}
                        />
                      </Form.Item>
                      <Text className={'text-xs font-semibold text-gray-500'}>KG</Text>
                    </Space>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
};

CreateBatchModal.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  onBatchCreated: PropTypes.func,
};

CreateBatchModal.defaultProps = {
  onBatchCreated: null,
};

export default CreateBatchModal;
